import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Card, 
  List, 
  Progress, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  Empty,
  Spin,
  Alert,
  Tooltip,
  Row,
  Col,
  Statistic,
  Checkbox,
  Input,
  DatePicker,
  Select,
  Popconfirm,
  message
} from 'antd';
import { 
  EyeOutlined, 
  DownloadOutlined, 
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  ExportOutlined,
  DeleteOutlined,
  RedoOutlined
} from '@ant-design/icons';
import { videoApi } from '../services/api';
import socketService from '../services/socket';
import { getString } from '../utils/translations';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { convertFirestoreTimestamp, formatTimestamp } from '../utils/dateUtils';
import { computeDashboardStats } from '../utils/dashboardStats';
import { getVaProExportFilename } from '../utils/exportFilenames';
import ProgressDisplay from './ProgressDisplay';
import ExportProgressCard from './ExportProgressCard';
import RecentExportsPanel from './RecentExportsPanel';
import { useAuth } from '../contexts/AuthContext';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const JobDashboard = ({ socketConnected }) => {
  const navigate = useNavigate();
  const { isReadOnlyMode } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [opsLoading, setOpsLoading] = useState(false);
  const [activeExportJobId, setActiveExportJobId] = useState(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null); // For shift+click range selection
  const [filters, setFilters] = useState({
    statuses: [],
    createdFrom: null,
    createdTo: null,
    q: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });
  // Server-side pagination state (page is 1-based for Ant Design's <List> pagination)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filter setter that also resets pagination to page 1. Colocating the reset
  // with the filter mutation keeps filter-change intent obvious at every call
  // site and avoids a separate useEffect that can fire at unexpected times
  // (e.g. during the initial commit or when filters are reassigned to the
  // same value).
  const setFiltersAndReset = (updater) => {
    setFilters(updater);
    setPage(1);
  };

  // Fetch jobs using React Query (server-side pagination + server-side totals)
  const { 
    data: jobsData, 
    isLoading, 
    error, 
    refetch 
  } = useQuery(
    ['jobs', filters, page, pageSize],
    () => videoApi.getAllJobs(pageSize, {
      offset: (page - 1) * pageSize,
      statuses: filters.statuses,
      createdFrom: filters.createdFrom ? filters.createdFrom.toDate().toISOString() : undefined,
      createdTo: filters.createdTo ? filters.createdTo.toDate().toISOString() : undefined,
      q: filters.q || undefined,
    }),
    {
      keepPreviousData: true,
      refetchInterval: socketConnected ? false : 30000, // Auto-refresh every 30s if no socket
      onSuccess: (data) => {
        const jobsList = data?.jobs || [];
        setJobs(jobsList);
        // computeDashboardStats prefers server `total` + `status_counts` when
        // available and falls back to per-page counts for older web-api
        // revisions. See src/utils/dashboardStats.js for the full contract.
        setStats(computeDashboardStats(jobsList, data));
      }
    }
  );

  // Subscribe to real-time updates for processing jobs
  useEffect(() => {
    if (socketConnected && jobs.length > 0) {
      const processingJobs = jobs.filter(job => 
        job.status === 'processing' || job.status === 'uploading'
      );
      
      processingJobs.forEach(job => {
        socketService.subscribeToJob(job.job_id, (progressData) => {
          handleJobUpdate(job.job_id, progressData);
        });
      });

      // Cleanup subscriptions
      return () => {
        processingJobs.forEach(job => {
          socketService.unsubscribeFromJob(job.job_id);
        });
      };
    }
  }, [socketConnected, jobs]);

  // Handle real-time job updates
  const handleJobUpdate = (jobId, progressData) => {
    let statusChanged = false;
    setJobs(prevJobs => {
      const updatedJobs = prevJobs.map(job => {
        if (job.job_id === jobId) {
          const nextStatus = progressData.status || job.status;
          if (nextStatus !== job.status) {
            statusChanged = true;
          }
          return {
            ...job,
            status: nextStatus,
            progress: progressData.progress || job.progress,
            processed_frames: progressData.processed_frames || job.processed_frames,
            total_frames: progressData.total_frames || job.total_frames,
            updated_at: new Date().toISOString()
          };
        }
        return job;
      });
      return updatedJobs;
    });

    // When a job transitions (e.g. processing -> completed), ask the server for
    // fresh totals/status_counts so the stat cards stay correct across pagination.
    if (statusChanged) {
      refetch();
    }
  };

  // Download export CSV with all blendshapes
  const handleDownload = async (jobId, filename) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const exportResult = await videoApi.exportResults(jobId, { tz });
      if (exportResult.kind === 'queued') {
        setActiveExportJobId(exportResult.exportJob.export_job_id);
        message.info('Large export queued. It will appear in Recent Exports when ready.');
        return;
      }

      const blob = exportResult.blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getVaProExportFilename(filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedJobIds.length === 0) return;
    try {
      setOpsLoading(true);
      await videoApi.batchDelete(selectedJobIds);
      message.success(getString('pipeline.msg.deleted_jobs', `Deleted ${selectedJobIds.length} job(s)`, { count: selectedJobIds.length }));
      setSelectedJobIds([]);
      await refetch();
    } catch (e) {
      console.error('Batch delete failed', e);
      message.error(getString('pipeline.msg.batch_delete_failed', 'Batch delete failed'));
    } finally {
      setOpsLoading(false);
    }
  };

  const handleBatchRetry = async () => {
    if (selectedJobIds.length === 0) return;
    try {
      setOpsLoading(true);
      await videoApi.batchRetry(selectedJobIds);
      message.success(`Retried ${selectedJobIds.length} job(s)`);
      setSelectedJobIds([]);
      await refetch();
    } catch (e) {
      console.error('Batch retry failed', e);
      message.error('Batch retry failed');
    } finally {
      setOpsLoading(false);
    }
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    const statusConfig = {
      uploading: { color: 'blue', icon: <LoadingOutlined />, text: getString('pipeline.jobs.status_uploading', 'Uploading') },
      processing: { color: 'orange', icon: <ClockCircleOutlined />, text: getString('pipeline.jobs.processing', 'Processing') },
      completed: { color: 'green', icon: <CheckCircleOutlined />, text: getString('pipeline.jobs.completed', 'Completed') },
      error: { color: 'red', icon: <ExclamationCircleOutlined />, text: getString('pipeline.jobs.failed', 'Failed') }
    };
    
    return statusConfig[status] || statusConfig.error;
  };

  // Render job item with selection checkbox
  const renderJobItem = (job, index) => {
    const statusDisplay = getStatusDisplay(job.status);
    const isProcessing = job.status === 'processing' || job.status === 'uploading';
    // P28 FIX (Dec 2025): Only allow download when export_ready=true
    // status='completed' can be set BEFORE all frames are in DB (race condition)
    // export_ready=true is set ONLY when all frames confirmed in DB + Redis queues empty
    const canDownload = job.status === 'completed' && job.export_ready === true;

    // Handle checkbox click with keyboard modifiers
    const handleCheckboxChange = (e) => {
      const isChecked = e.target.checked;
      const shiftKey = e.nativeEvent.shiftKey;
      const cmdOrCtrlKey = e.nativeEvent.metaKey || e.nativeEvent.ctrlKey;

      if (shiftKey && lastSelectedIndex !== null) {
        // Shift+Click: Select range
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const rangeIds = jobs.slice(start, end + 1).map(j => j.job_id);
        
        setSelectedJobIds(prev => {
          const newSelection = new Set(prev);
          rangeIds.forEach(id => newSelection.add(id));
          return Array.from(newSelection);
        });
        setLastSelectedIndex(index);
      } else if (cmdOrCtrlKey) {
        // Cmd/Ctrl+Click: Toggle single item
        setSelectedJobIds(prev => {
          if (prev.includes(job.job_id)) {
            return prev.filter(id => id !== job.job_id);
          } else {
            return [...prev, job.job_id];
          }
        });
        setLastSelectedIndex(index);
      } else {
        // Normal click: Single selection
        setSelectedJobIds(prev => {
          if (isChecked) {
            return Array.from(new Set([...prev, job.job_id]));
          } else {
            return prev.filter(id => id !== job.job_id);
          }
        });
        setLastSelectedIndex(index);
      }
    };

    return (
      <List.Item
        key={job.job_id}
        actions={[
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => navigate(`/jobs/${job.job_id}`)}
          >
            {getString('pipeline.jobs.view', 'View')}
          </Button>,
          canDownload && (
            <Button 
              type="text" 
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(job.job_id, job.filename)}
            >
              {getString('pipeline.jobs.download', 'Download')}
            </Button>
          )
        ].filter(Boolean)}
      >
        <List.Item.Meta
          title={
            <Space>
              <Checkbox
                checked={selectedJobIds.includes(job.job_id)}
                onChange={handleCheckboxChange}
              />
              <Text strong className="job-filename" style={{ whiteSpace: 'nowrap' }}>{job.filename}</Text>
              <Tag icon={statusDisplay.icon} color={statusDisplay.color}>
                {statusDisplay.text}
              </Tag>
            </Space>
          }
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {isProcessing && (
                <ProgressDisplay 
                  jobId={job.job_id}
                  jobData={job}
                  size="small"
                  showText={false}
                  showIcon={true}
                  showSSEConnection={false} // Disabled since SSE is globally disabled
                  showDetails={false}
                  // useSSE will use global config (disabled)
                  className="job-progress"
                />
              )}
              
              <Row gutter={16}>
                <Col span={8}>
                  <Text type="secondary">
                    {getString('pipeline.jobs.created', 'Created:')} {formatTimestamp(job.created_at, 'YYYY/MM/DD HH:mm:ss')}
                  </Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">
                    {getString('pipeline.jobs.updated', 'Updated:')} {dayjs(convertFirestoreTimestamp(job.updated_at)).fromNow()}
                  </Text>
                </Col>
                {job.total_frames > 0 && (
                  <Col span={8}>
                    <Text type="secondary">
                      {getString('pipeline.jobs.frames', 'Frames:')} {job.processed_frames || 0}/{job.total_frames}
                    </Text>
                  </Col>
                )}
              </Row>
            </Space>
          }
        />
      </List.Item>
    );
  };

  if (error) {
    return (
      <Alert
        message={getString('pipeline.jobs.failed_to_load', 'Failed to Load Jobs')}
        description={error.response?.data?.error || error.message}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => refetch()}>
            {getString('pipeline.jobs.refresh', 'Retry')}
          </Button>
        }
      />
    );
  }

  // Calculate export-ready jobs count for selection button
  const exportReadyCount = jobs.filter(j => j.status === 'completed' && j.export_ready === true).length;
  
  // Check if any selected job is not export-ready (for warning tooltip)
  const hasNonReadySelection = selectedJobIds.some(id => {
    const job = jobs.find(j => j.job_id === id);
    return !job || job.status !== 'completed' || job.export_ready !== true;
  });

  return (
    <div className="page-container">
      <div style={{ marginBottom: '2rem' }}>
        <div className="jd-header">
          <Title level={2} className="jd-title">{getString('pipeline.jobs.title', 'Job Dashboard')}</Title>
          <div className="jd-actions">
            {/* Bulk Selection Controls */}
            <Space style={{ marginRight: '1rem', marginBottom: '0.5rem' }}>
              <Button 
                size="small"
                onClick={() => {
                  // Select current page only (first 50 visible jobs)
                  const pageJobIds = jobs.slice(0, 50).map(j => j.job_id);
                  setSelectedJobIds(pageJobIds);
                  message.success(`Selected ${pageJobIds.length} job(s) on current page`);
                }}
                disabled={jobs.length === 0}
              >
                {getString('pipeline.jobs.select_page', 'Select Page')} ({Math.min(jobs.length, 50)})
              </Button>

              <Button 
                size="small"
                onClick={() => {
                  setSelectedJobIds(jobs.map(j => j.job_id));
                  message.success(`Selected all ${jobs.length} job(s)`);
                }}
                disabled={jobs.length === 0}
              >
                {getString('pipeline.jobs.select_all', 'Select All')} ({jobs.length})
              </Button>
              
              <Button 
                size="small"
                onClick={() => {
                  const exportReadyIds = jobs
                    .filter(j => j.status === 'completed' && j.export_ready === true)
                    .map(j => j.job_id);
                  setSelectedJobIds(exportReadyIds);
                  message.success(`Selected ${exportReadyIds.length} export-ready job(s)`);
                }}
                disabled={exportReadyCount === 0}
              >
                {getString('pipeline.jobs.select_export_ready', 'Select Export-Ready')} ({exportReadyCount})
              </Button>
              
              <Button 
                size="small"
                onClick={() => setSelectedJobIds([])}
                disabled={selectedJobIds.length === 0}
              >
                {getString('pipeline.jobs.clear_selection', 'Clear Selection')}
              </Button>
            </Space>

            {/* Quick Filter Presets */}
            <Space style={{ marginRight: '1rem' }}>
              <Button.Group>
                <Button 
                  size="small"
                  type={filters.statuses.length === 0 ? 'primary' : 'default'}
                  onClick={() => setFiltersAndReset(prev => ({ ...prev, statuses: [] }))}
                >
                  {getString('pipeline.jobs.filter_all', 'All Jobs')}
                </Button>
                <Button 
                  size="small"
                  type={filters.statuses.includes('processing') && filters.statuses.length === 1 ? 'primary' : 'default'}
                  onClick={() => setFiltersAndReset(prev => ({ ...prev, statuses: ['processing'] }))}
                >
                  {getString('pipeline.jobs.filter_active', 'Active')}
                </Button>
                <Button 
                  size="small"
                  type={filters.statuses.includes('completed') && filters.statuses.length === 1 ? 'primary' : 'default'}
                  onClick={() => setFiltersAndReset(prev => ({ ...prev, statuses: ['completed'] }))}
                >
                  {getString('pipeline.jobs.filter_ready_export', 'Ready to Export')}
                </Button>
                <Button 
                  size="small"
                  type={filters.statuses.includes('error') && filters.statuses.length === 1 ? 'primary' : 'default'}
                  onClick={() => setFiltersAndReset(prev => ({ ...prev, statuses: ['error'] }))}
                >
                  {getString('pipeline.jobs.filter_failed', 'Failed')}
                </Button>
                <Button 
                  size="small"
                  type={filters.createdFrom && dayjs().diff(filters.createdFrom, 'day') === 0 ? 'primary' : 'default'}
                  onClick={() => setFiltersAndReset(prev => ({ 
                    ...prev, 
                    createdFrom: dayjs().startOf('day'), 
                    createdTo: dayjs().endOf('day') 
                  }))}
                >
                  {getString('pipeline.jobs.filter_today', 'Today')}
                </Button>
              </Button.Group>
            </Space>

            {/* Advanced Filters */}
            <Space style={{ marginRight: '1rem' }}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Statuses"
                style={{ minWidth: 180 }}
                value={filters.statuses}
                onChange={(vals) => setFiltersAndReset(prev => ({ ...prev, statuses: vals }))}
                options={[
                  { label: 'Uploading', value: 'uploading' },
                  { label: 'Processing', value: 'processing' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Failed', value: 'error' },
                ]}
              />
              <DatePicker.RangePicker
                value={[filters.createdFrom, filters.createdTo]}
                onChange={(range) => setFiltersAndReset(prev => ({
                  ...prev,
                  createdFrom: range?.[0] || null,
                  createdTo: range?.[1] || null,
                }))}
              />
              <Input.Search
                allowClear
                placeholder={getString('pipeline.jobs.search_placeholder', 'Search filename or error')}
                onSearch={(val) => setFiltersAndReset(prev => ({ ...prev, q: val }))}
                style={{ width: 240 }}
              />
            </Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => refetch()}
              loading={isLoading}
            >
              {getString('pipeline.jobs.refresh', 'Refresh')}
            </Button>
            {!isReadOnlyMode && (
              <Button
                type="primary"
                onClick={() => navigate('/upload')}
                className="jd-action"
              >
                {getString('pipeline.jobs.upload_new', 'Upload New Video')}
              </Button>
            )}
            {!isReadOnlyMode && (
              <Popconfirm
                title="Delete selected jobs?"
                description="This will permanently remove the jobs and their results."
                onConfirm={handleBatchDelete}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={selectedJobIds.length === 0}
                  loading={opsLoading}
                  className="jd-action"
                >
                  {getString('pipeline.jobs.delete_selected', 'Delete Selected')}
                </Button>
              </Popconfirm>
            )}
            {!isReadOnlyMode && (
              <Button
                icon={<RedoOutlined />}
                disabled={selectedJobIds.length === 0}
                loading={opsLoading}
                className="jd-action"
                onClick={handleBatchRetry}
              >
                {getString('pipeline.jobs.retry_selected', 'Retry Selected')}
              </Button>
            )}
            <Tooltip title={selectedJobIds.length > 0 && !selectedJobIds.every(id => {
              const job = jobs.find(j => j.job_id === id);
              return job && job.export_ready === true;
            }) ? 'Some selected jobs are not ready for export' : ''}>
              <Button
                icon={<ExportOutlined />}
                disabled={selectedJobIds.length === 0}
                loading={batchLoading}
                className="jd-action"
                onClick={async () => {
                  try {
                    setBatchLoading(true);
                    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    
                    // Use async export API
                    const exportJob = await videoApi.createExportJob(selectedJobIds, { format: 'csv', tz });
                    
                    message.success(getString('pipeline.msg.export_created', `Export job created for ${selectedJobIds.length} video(s)`, { count: selectedJobIds.length }));
                    setActiveExportJobId(exportJob.export_job_id);
                    
                    // Clear selection after initiating export
                    setSelectedJobIds([]);
                    
                  } catch (e) {
                    console.error('Batch export failed', e);
                    message.error('Failed to create export job');
                  } finally {
                    setBatchLoading(false);
                  }
                }}
              >
                {getString('pipeline.jobs.batch_export_zip', 'Batch Export (ZIP)')}
              </Button>
            </Tooltip>
          </div>
        </div>

        {isReadOnlyMode && (
          <Alert
            message={getString('pipeline.read_only.title', 'Read-only mode')}
            description={getString(
              'pipeline.read_only.description',
              'You can view and export existing results, but uploads, starting new processing jobs, retries, and deletion are disabled for this organization.'
            )}
            type="warning"
            showIcon
            style={{ marginBottom: '1rem' }}
          />
        )}

        {/* Selection Status Banner */}
        {selectedJobIds.length > 0 && (
          <Alert
            message={`${selectedJobIds.length} job(s) selected`}
            type="info"
            showIcon
            closable
            onClose={() => setSelectedJobIds([])}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
            action={
              hasNonReadySelection && (
                <Text type="warning" style={{ fontSize: '12px' }}>
                  {selectedJobIds.filter(id => {
                    const job = jobs.find(j => j.job_id === id);
                    return !job || job.status !== 'completed' || job.export_ready !== true;
                  }).length} not ready
                </Text>
              )
            }
          />
        )}

        {/* Statistics */}
        <Row gutter={16} style={{ marginBottom: '2rem' }}>
          <Col xs={12} md={6}>
            <Card>
              <Statistic title={getString('pipeline.jobs.total_jobs', 'Total Jobs')} value={stats.total} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic 
                title={getString('pipeline.jobs.processing', 'Processing')} 
                value={stats.processing} 
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic 
                title={getString('pipeline.jobs.completed', 'Completed')} 
                value={stats.completed} 
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic 
                title={getString('pipeline.jobs.failed', 'Failed')} 
                value={stats.failed} 
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Export Progress Card */}
        {activeExportJobId && (
          <ExportProgressCard
            exportJobId={activeExportJobId}
            onComplete={() => {
              message.success('Export completed successfully!');
            }}
            onError={(error) => {
              message.error(`Export failed: ${error}`);
            }}
          />
        )}

      </div>

      <Card title={getString('pipeline.jobs.filter_all', 'All Jobs')} style={{ minHeight: '400px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Spin size="large" />
            <div style={{ marginTop: '1rem' }}>
              <Text type="secondary">{getString('pipeline.jobs.loading', 'Loading jobs...')}</Text>
            </div>
          </div>
        ) : jobs.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {isReadOnlyMode
                  ? getString('pipeline.jobs.no_jobs_read_only', 'No existing jobs found for this organization.')
                  : (
                    <>
                      {getString('pipeline.jobs.no_jobs', 'No jobs found. Upload your first video')}. <Button type="link" onClick={() => navigate('/upload')}>{getString('pipeline.jobs.upload_first_video', 'Upload your first video')}</Button>
                    </>
                  )}
              </span>
            }
          />
        ) : (
          <List
            header={
              // NOTE: this "Select All" is intentionally per-page ("Visible"):
              // `jobs` is the current paginated slice (up to `pageSize`) while
              // `stats.total` is the full filtered count from the server.
              // They will differ whenever the filtered dataset is larger than
              // one page — that's by design (selecting 1000s of jobs at once
              // would also blow up the batch-delete/batch-retry endpoints).
              <Checkbox
                checked={selectedJobIds.length === jobs.length && jobs.length > 0}
                indeterminate={selectedJobIds.length > 0 && selectedJobIds.length < jobs.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedJobIds(jobs.map(j => j.job_id));
                    message.info(`Selected all ${jobs.length} jobs`);
                  } else {
                    setSelectedJobIds([]);
                  }
                }}
              >
                Select All Visible Jobs
              </Checkbox>
            }
            dataSource={jobs}
            renderItem={(job, index) => renderJobItem(job, index)}
            pagination={{
              current: page,
              pageSize,
              total: stats.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '25', '50', '100', '200', '500'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                }
              },
              onShowSizeChange: (_current, nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              },
            }}
          />
        )}
      </Card>

      {/* Recent Exports Panel */}
      <RecentExportsPanel onRefresh={refetch} />
    </div>
  );
};

export default JobDashboard; 